import { addDays, format, parseISO } from 'date-fns'
import { Plus, Trash2 } from 'lucide-react'
import type { SalesPacketDto } from '@lib/dto/sales-packet.dto'
import type { SalesItemDto, SalesPriceListDto } from '@lib/dto/sales-price-list.dto'
import type { SalesLineGroup } from '@lib/enums/sales-packet.enum'
import type { PacketLineLike } from '@lib/helpers/sales-packet'
import { packagePrice } from '@lib/helpers/sales-prices'
import type { PacketLineInput } from '@lib/input/sales-packet.input'
import { calendarDay, type CalendarDay } from '@lib/validators/temporal.validator'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { centsToInput, inputToCents } from '@/lib/price'
import { newKey } from './scheduleDraft'

/** A line as it's being typed: money and quantity as text until sent. */
export interface LineDraft {
  key: string
  group: SalesLineGroup
  itemId: string | null
  name: string
  description: string
  unit: string
  quantity: string
  taxable: boolean
  serviceCharged: boolean
}

const GROUPS: { value: SalesLineGroup; label: string }[] = [
  { value: 'package', label: 'Package' },
  { value: 'add_on', label: 'Add-on' },
  { value: 'included', label: 'Included' },
]

function groupOf(value: string): SalesLineGroup {
  return value === 'package' || value === 'included' ? value : 'add_on'
}

/** A draft as a line to send, or why it isn't one yet. */
export function lineOf(d: LineDraft): PacketLineInput | string {
  const name = d.name.trim() === '' ? 'every line' : d.name
  const unitCents = inputToCents(d.unit)
  if (unitCents === null) return `Enter a price for ${name}.`
  const quantity = Number.parseInt(d.quantity, 10)
  if (!Number.isInteger(quantity) || quantity < 1 || String(quantity) !== d.quantity.trim()) return `Enter how many of ${name}.`
  return { group: d.group, itemId: d.itemId, name: d.name.trim(), description: d.description, unitCents, quantity, taxable: d.taxable, serviceCharged: d.serviceCharged }
}

/** Every draft as a line, or the first reason one isn't. */
export function linesOf(drafts: readonly LineDraft[]): PacketLineInput[] | string {
  const lines: PacketLineInput[] = []
  for (const d of drafts) {
    const line = lineOf(d)
    if (typeof line === 'string') return line
    lines.push(line)
  }
  return lines
}

/** The lines that read, for the running totals. */
export function quoteLines(drafts: readonly LineDraft[]): PacketLineLike[] {
  return drafts.flatMap((d) => {
    const line = lineOf(d)
    return typeof line === 'string' ? [] : [{ ...line, key: d.key }]
  })
}

/** The day `days` after `day`, as a calendar day. */
export function plusDays(day: CalendarDay, days: number): CalendarDay {
  return calendarDay(format(addDays(parseISO(day), days), 'yyyy-MM-dd'))
}

/**
 * A price list item as a line: a package priced for the date, a per-guest
 * price times the guests. `group` is where it goes; an invoice puts
 * everything under included.
 */
export function lineFromItem(item: SalesItemDto, priceList: SalesPriceListDto, date: CalendarDay | '', guestCount: number, group?: SalesLineGroup): LineDraft {
  const seasons = priceList.seasons.map((s) => ({ id: s.id, name: s.name, from: s.startsOn, to: s.endsOn }))
  let unit = centsToInput(item.priceCents)
  if (item.kind === 'package') unit = date === '' ? '' : centsToInput(packagePrice(item.prices, seasons, date))
  const own: SalesLineGroup = item.kind === 'package' ? 'package' : item.kind === 'per_guest' ? 'included' : 'add_on'
  return {
    key: newKey(),
    group: group ?? own,
    itemId: item.id,
    name: item.name,
    description: item.description,
    unit,
    quantity: item.kind === 'per_guest' ? String(Math.max(guestCount, 1)) : '1',
    taxable: item.taxable,
    serviceCharged: item.serviceCharged,
  }
}

/** A line of a signed agreement, as what a change to it starts from: included, as it was agreed. */
export function lineFromAgreed(line: SalesPacketDto['lines'][number]): LineDraft {
  return {
    key: newKey(),
    group: 'included',
    itemId: line.itemId,
    name: line.name,
    description: line.description,
    unit: centsToInput(line.unitCents),
    quantity: String(line.quantity),
    taxable: line.taxable,
    serviceCharged: line.serviceCharged,
  }
}

interface PacketLinesProps {
  lines: readonly LineDraft[]
  onChange: (lines: LineDraft[]) => void
  /** What the price list offers here. */
  items: readonly SalesItemDto[]
  onAddItem: (item: SalesItemDto) => void
  /** Let each line be a package, add-on or included; off, everything is included. */
  withGroups: boolean
  empty: string
}

/** The lines of a proposal or invoice, each editable, added from the price list or typed. */
export function PacketLines({ lines, onChange, items, onAddItem, withGroups, empty }: PacketLinesProps) {
  const update = (key: string, patch: Partial<LineDraft>): void => onChange(lines.map((l) => (l.key === key ? { ...l, ...patch } : l)))
  return (
    <>
      {lines.length === 0 ? <p className="m-0 text-sm text-ink-muted">{empty}</p> : null}
      <ul className="m-0 list-none space-y-2 p-0">
        {lines.map((l) => (
          <li key={l.key} className="flex flex-wrap items-center gap-2">
            {withGroups ? (
              <Select size="sm" aria-label="Kind" value={l.group} onChange={(e) => update(l.key, { group: groupOf(e.target.value) })}>
                {GROUPS.map((g) => (
                  <option key={g.value} value={g.value}>
                    {g.label}
                  </option>
                ))}
              </Select>
            ) : null}
            <Input size="sm" aria-label="Name" className="min-w-40 flex-1" value={l.name} onChange={(e) => update(l.key, { name: e.target.value })} />
            <div className="flex items-center gap-1">
              <span className="text-sm text-ink-muted">$</span>
              <Input size="sm" inputMode="decimal" aria-label="Price" className="w-24 text-right tabular-nums" value={l.unit} onChange={(e) => update(l.key, { unit: e.target.value })} />
              <span className="text-sm text-ink-muted">×</span>
              <Input
                size="sm"
                inputMode="numeric"
                aria-label="Quantity"
                className="w-16 text-right tabular-nums"
                value={l.quantity}
                onChange={(e) => update(l.key, { quantity: e.target.value })}
              />
            </div>
            <label className="flex items-center gap-1.5 text-xs text-ink-muted">
              <Checkbox checked={l.taxable} onCheckedChange={(taxable) => update(l.key, { taxable })} />
              Taxed
            </label>
            <label className="flex items-center gap-1.5 text-xs text-ink-muted">
              <Checkbox checked={l.serviceCharged} onCheckedChange={(serviceCharged) => update(l.key, { serviceCharged })} />
              Service charge
            </label>
            <Button variant="ghost" size="sm" aria-label={`Remove ${l.name}`} onClick={() => onChange(lines.filter((x) => x.key !== l.key))}>
              <Trash2 className="h-4 w-4" />
            </Button>
          </li>
        ))}
      </ul>
      <div className="flex flex-wrap items-center gap-2">
        <Select
          size="sm"
          aria-label="Add from the price list"
          value=""
          onChange={(e) => {
            const item = items.find((i) => i.id === e.target.value)
            if (item !== undefined) onAddItem(item)
          }}
        >
          <option value="">Add from the price list…</option>
          {items.map((item) => (
            <option key={item.id} value={item.id}>
              {item.name}
            </option>
          ))}
        </Select>
        <Button
          variant="outline"
          size="sm"
          onClick={() =>
            onChange([
              ...lines,
              { key: newKey(), group: withGroups ? 'add_on' : 'included', itemId: null, name: '', description: '', unit: '', quantity: '1', taxable: true, serviceCharged: true },
            ])
          }
        >
          <Plus className="h-3.5 w-3.5" />
          Type a line
        </Button>
      </div>
    </>
  )
}
